import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated, type StaffRoleType } from 'api/v1Helpers';

export interface StaffMember {
    id: string;
    role: StaffRoleType;
    createdAt: string;
    user: {
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        avatarUrl: string | null;
    };
}

export interface Invitation {
    id: string;
    email: string;
    role: StaffRoleType;
    status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
    expiresAt: string;
    createdAt: string;
}

/**
 * List parish staff. Backend `GET .../staff` returns a full set (no query paging);
 * we still send page/pageSize for contract consistency with siblings.
 */
export async function fetchStaff(parishId: string, opts?: { page?: number; pageSize?: number }): Promise<Paginated<StaffMember>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 100);
    const res = await appClient.get<Paginated<StaffMember>>(`parishes/${parishId}/staff?${params.toString()}`);
    return {
        items: res.items || [],
        total: res.total ?? (res.items || []).length,
        page: res.page ?? opts?.page ?? 1,
        pageSize: res.pageSize ?? opts?.pageSize ?? 100,
    };
}

export async function addStaff(parishId: string, userId: string, role: StaffRoleType): Promise<StaffMember> {
    return appClient.post<StaffMember>(`parishes/${parishId}/staff`, { userId, role });
}

export async function removeStaffMember(parishId: string, userId: string): Promise<void> {
    await appClient.delete(`parishes/${parishId}/staff/${userId}`);
}

export async function fetchInvitations(
    parishId: string,
    opts?: { page?: number; pageSize?: number; status?: string }
): Promise<Paginated<Invitation>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 50, {
        status: opts?.status,
    });
    return appClient.get<Paginated<Invitation>>(`parishes/${parishId}/invitations?${params.toString()}`);
}

export async function createInvitation(parishId: string, email: string, role: StaffRoleType): Promise<Invitation> {
    return appClient.post<Invitation>(`parishes/${parishId}/invitations`, {
        email,
        role,
    });
}

export async function revokeInvitation(parishId: string, invitationId: string): Promise<void> {
    await appClient.delete(`parishes/${parishId}/invitations/${invitationId}`);
}

/** Provisioned staff account (primary Create Staff path). Never returns a password. */
export type ProvisionedStaffAccount = {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: StaffRoleType;
    parishId: string;
    status: string;
    mustChangePassword: boolean;
    createdAt: string;
    kind: 'provisioned' | 'granted';
};

export async function provisionStaffAccount(
    parishId: string,
    payload: { firstName: string; lastName: string; email: string; role: StaffRoleType }
): Promise<ProvisionedStaffAccount> {
    return appClient.post<ProvisionedStaffAccount>(`parishes/${parishId}/staff/accounts`, payload);
}
